/**
 * Axios configuration and interceptor setup for API communication
 * Handles authentication, error handling, and automatic token management
 * Provides a pre-configured Axios instance with automatic authentication headers,
 * company ID headers, and centralized error handling including 401/422 responses
 *
 * @public
 * @useWhen Making API calls; Need automatic token management and error handling
 */

import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import axios from 'axios'

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** When true, omit X-Company-ID (e.g. client-scoped pricing that must not use session company). */
    skipCompanyHeader?: boolean
  }
}
import { useAppSettingsStore } from 'src/CrudManager/Core/stores/useAppSettingsStore'
import { useGlobalStore } from 'src/CrudManager/Core/stores/useGlobalStore'
import { useUserStore } from 'src/CrudManager/Core/stores/useUserStore'
import { getApiErrorMessage } from 'src/CrudManager/Core/Utils/apiError'
import { runtimeEnv } from 'src/CrudManager/Core/Utils/runtimeConfig'
import { showErrorNotification, showWarningNotification } from 'src/CrudManager/Core/Utils/ToastNotification'
import type { Router } from 'vue-router'

/**
 * Pre-configured Axios instance with `/api/` base URL
 * Automatically prepends `/api/` to all requests and includes authentication headers
 *
 * **Base URL**: `/api/` is automatically prepended to all requests.
 *
 * @public
 * @example
 * ```typescript
 * // This request will go to: /api/users
 * axiosApi.get('/users')
 *
 * // This request will go to: /api/auth/login
 * axiosApi.post('/auth/login', { email, password })
 * ```
 *
 * @remarks
 * - Authentication headers are automatically added via request interceptors
 * - Company ID and app version headers are included when available
 * - Response interceptors handle authentication errors and show notifications
 *
 *
 * @see {@link setApiHeaders} for header configuration
 * @see {@link setupAxios} for error handling setup
 * @see {@link AxiosInstance} for AxiosInstance type
 */
export const axiosApi = axios.create({
  baseURL: runtimeEnv('API_BASE_URL', import.meta.env.API_BASE_URL) || '/api',
  withCredentials: false,
}) as AxiosInstance
/**
 * Sets the API token, company ID, and app version in the request headers if available
 * Called automatically by request interceptor before each API call
 *
 * @param config - Axios request configuration to modify
 * @internal
 * @returns void
 */
const setApiHeaders = (config: AxiosRequestConfig): void => {
  const userStore = useUserStore()
  const appSettingsStore = useAppSettingsStore()
  const globalStore = useGlobalStore()

  if (userStore.token) {
    axiosApi.defaults.headers.common['Authorization'] = `Bearer ${userStore.token}`
    config.headers.Authorization = `Bearer ${userStore.token}`
  } else {
    console.warn('Authorization token not available during request setup.')
  }

  if (! config.skipCompanyHeader) {
    if (appSettingsStore.currentCompany && appSettingsStore.currentCompany.id) {
      config.headers['X-Company-ID'] = appSettingsStore.currentCompany.id
    } else {
      console.warn('Company ID not available during request setup.')
    }
  }

  if (globalStore.appVersion) {
    config.headers['X-app-version'] = globalStore.appVersion
  } else {
    console.warn('App version not available during request setup.')
  }
}

// Setup request interceptor
axiosApi.interceptors.request.use((config) => {
  setApiHeaders(config)
  return config
})

let isInterceptorSetup = false

/**
 * Initializes axios with router for authentication redirects and error handling
 * Sets up response interceptors to handle 401 (unauthorized) and 422 (validation) errors
 * Automatically redirects to login on authentication failures
 *
 * @param router - Vue router instance for navigation
 * @internal
 * @example
 * ```typescript
 * setupAxios(router)
 * ```
 */
export const setupAxios = (router: Router): void => {
  const userStore = useUserStore()
  if (isInterceptorSetup) return
  isInterceptorSetup = true

  axiosApi.interceptors.response.use(
    async function (response) {
      if (response.data && response.data.message === 'Unauthenticated.') {
        await userStore.logout()
        localStorage.clear()
        router.push({ path: '/login' })
      }
      return response
    },
    async (error) => {
      const status = error.response?.status || 'OFFLINE'
      const url = error.config?.url || error.response?.config?.url

      if (status === 401 && !url?.includes('broadcasting/auth')) {
        try {
          await userStore.logout()
          localStorage.clear()

          if (url !== 'user' && router.currentRoute.value.fullPath !== '/login') {
            router.push({ name: 'login' })
            showErrorNotification('Votre session as expirée')
          }
        } catch (logoutError) {
          console.error(logoutError)
        }
      }
      // Only the normalised message reaches the user: response bodies can quote a
      // third-party API. The rejection is logged so developers keep the full payload.
      if (status === 422 || status === 'OFFLINE') {
        console.error('[API]', error.config?.method?.toUpperCase(), url, error.response?.data ?? error)

        const message = getApiErrorMessage(error)

        if (status === 422) showWarningNotification(message)
        else showErrorNotification(message)
      }

      return Promise.reject(error)
    },
  )
}
